import { useCallback, useEffect, useMemo } from "react";
import * as React from "react";
import { useSnapshot } from "valtio";

import { DeckGlClickEvent, mapStore } from "src/js/stores/map/store";
import type { ClickedLatLng } from "react-migration/lib/map/typings";

import { useMapLayer } from "react-migration/lib/map/useMapLayer";
import { createFilters, createZooplaTileFilters } from "../../util/Filters";
import { disabledEPCFields, disabledZooplaFields } from "../../util/DisabledFilters";
import { hiddenFieldsByFeature } from "../../util/hiddenFields";
import { isZoopla } from "../../util/isZoopla";
import { userStore } from "src/js/stores/user/store";
import { AggregatedZooplaComparableTile } from "../../typings/AggregatedZooplaComparableTile";
import { ComparableTile } from "react-migration/lib/typings/ComparablesTile";
import { CorePriceModes, ZooplaPriceModes } from "../../typings/PriceModes";
import { ComparablesLayer } from "./ComparablesLayer/ComparablesLayer";
import { ComparablesFilterFields } from "../Filters/FiltersWithClear";
import { ZooplaLayer } from "./ComparablesLayer/ZooplaLayer";
import { comparablesStore } from "src/js/stores/comparables/store";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";

interface MapLayerProps {
  /** distribution of percentile groups [p0: number, p5: number, p25: number p75: number p95: number, p100: number ] */
  distribution?: number[];
  /** Minimum zoom level to display parcels (deck.gl value is 1 lower than google maps) */
  minZoom?: number;
  /** Maximum zoom level to display parcels (deck.gl value is 1 lower than google maps) */
  maxZoom?: number;
  /** Returns event and latlng when parcel is selected  */
  onClickParcel?: (event: DeckGlClickEvent, latLng: ClickedLatLng, clickedUPRNs?: string[]) => void;
  /** Returns feature and latlng when parcel is hovered  */
  onHoverParcel?: (
    d: AggregatedZooplaComparableTile | ComparableTile | ComparableTile["properties"],
    latLng: ClickedLatLng
  ) => void;
  visible?: boolean;
  zOrder?: number;
}

const DEFAULT_MIN_ZOOM = 13;
const DEFAULT_MAX_ZOOM = 18;

/**
 * Component for displaying the Comparables layer on a map
 */
export const MapLayer: React.FC<MapLayerProps> = ({
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  onClickParcel,
  onHoverParcel,
  distribution,
  visible = true,
  zOrder,
}) => {
  const { mapType } = useSnapshot(mapStore.settings);
  const { unit: unitPreference } = useSnapshot(userStore.user.settings);
  const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const zooplaFullAccess = hasFeature(Feature.zooplaFullAccess);
  const { priceMode, priceSource, hmlrFilters, filters, hometrackFilters } = useSnapshot(comparablesStore);
  const useZooplaLayer = isZoopla(priceMode) && zooplaFullAccess;

  const filterFunctions = useMemo(() => {
    return useZooplaLayer
      ? createZooplaTileFilters(
          hometrackv2 ? hometrackFilters : filters,
          new Set<ComparablesFilterFields>([
            ...disabledZooplaFields,
            ...hiddenFieldsByFeature.flatMap((check) => (check[0]() ? Array.from(check[1]) : [])),
          ]),
          priceMode
        )
      : createFilters(
          hometrackv2 ? hmlrFilters : filters,
          new Set([
            ...disabledEPCFields,
            ...hiddenFieldsByFeature.flatMap((check) => (check[0]() ? Array.from(check[1]) : [])),
          ])
        );
  }, [hometrackFilters, hmlrFilters, priceMode, useZooplaLayer, filters, hometrackv2]);

  const dataAttributeName = "data-test-comparables-map-status";
  const onViewportLoading = useCallback(() => document.body.setAttribute(dataAttributeName, "loading"), []);
  const onViewportLoaded = useCallback(() => document.body.setAttribute(dataAttributeName, "loaded"), []);
  useEffect(() => () => document.body.removeAttribute(dataAttributeName), []);

  const getLayer = () => {
    if (useZooplaLayer) {
      return new ZooplaLayer({
        filterFunctions: filterFunctions as ReturnType<typeof createZooplaTileFilters>,
        id: "zoopla-layer",
        minZoom,
        maxZoom,
        onClick: ({ coordinate, object }, event) => {
          if (coordinate) {
            const [lng, lat] = coordinate;
            onClickParcel?.(
              event,
              { lat, lng },
              useZooplaLayer
                ? object.properties
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .filter((tile: any) => tile.properties.uprn)
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .map((tile: any) => String(tile.properties.uprn))
                : undefined
            );
          }
        },
        onHover: (pickInfo) => {
          if (pickInfo.object && pickInfo.coordinate) {
            const [lng, lat] = pickInfo.coordinate;
            onHoverParcel?.(pickInfo.object, {
              lat,
              lng,
            });
          }
        },
        onViewportLoaded,
        onViewportLoading,
        priceMode: priceMode as unknown as ZooplaPriceModes,
        priceSource: priceSource,
        unitPreference,
        visible,
      });
    }

    return new ComparablesLayer({
      filterFunctions: filterFunctions as ReturnType<typeof createFilters>,
      id: "comparables-layer",
      minZoom,
      maxZoom,
      // If they don't have zoopla full access, but have selected a Zoopla price mode,
      // we want to show nothing except the promo. Only for HTV2
      disabled: isZoopla(priceMode) && !zooplaFullAccess && hometrackv2,
      onClick: ({ coordinate }, event) => {
        if (coordinate) {
          const [lng, lat] = coordinate;
          onClickParcel?.(event, { lat, lng });
        }
      },
      onHover: (pickInfo) => {
        if (pickInfo.object && pickInfo.coordinate) {
          const [lng, lat] = pickInfo.coordinate;
          onHoverParcel?.(pickInfo.object, {
            lat,
            lng,
          });
        }
      },
      onViewportLoaded,
      onViewportLoading,
      priceMode: priceMode as unknown as CorePriceModes,
      distribution,
      unitPreference,
      visible,
    });
  };

  useMapLayer(
    () => ({
      layer: getLayer(),
      zOrder,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mapType,
      minZoom,
      maxZoom,
      onClickParcel,
      onHoverParcel,
      filters,
      hmlrFilters,
      hometrackFilters,
      priceMode,
      priceSource,
      distribution,
      unitPreference,
      visible,
      zOrder,
      useZooplaLayer,
    ],
    visible
  );

  return null;
};
